import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ActionsMenu from '../../../../components/ui/ActionsMenu';
import React, { useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useFetchData, useFetchFile } from '../../../../hooks';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { ReceivingReportRecord } from '../../../../types/ReceivingReport';
import { RRS_API } from '../../../../hooks/useReceivingReportHooks';
import { Button, Dropdown, MenuProps, Space, message } from 'antd';
import {
  SendOutlined,
  DownloadOutlined,
  EditOutlined,
  EllipsisOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import ReceivingReportInfoLoadingView from './ReceivingReportInfoLoadingView';
import { cn, isUserCanEdit, isUserCanCreate } from '../../../../lib/utils';
import PageHeaderAction from '../../../../components/PageHeaderAction';
import AuditCard from '../../../../components/AuditCard';
import TrackingChain from '../../../../components/TrackingChain';
import ErrorModal from '../../../../components/ui/modal/ErrorModal';
import ReceivingReportPrintTemplate from './ReceivingReportPrintTemplate';
import ReceivingReportInfoStatus from './ReceivingReportInfoStatus';
import ReceivingReportInfoContent from './ReceivingReportInfoContent';
import { StatusHistory } from '../../../../types';
import { useGenerateDeliveryReceipt } from '../../../../hooks/useDeliveryReceiptHooks';
import { useAuthStore } from '../../../../store';

const ReceivingReportInfoPage = () => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState<string | undefined>('');
  const componentRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });
  const { user } = useAuthStore();
  const { mutate: generateDr } = useGenerateDeliveryReceipt();

  const { data, isLoading, error, isError } =
    useFetchData<ReceivingReportRecord>(`${RRS_API}/${params.id}`);
  const { data: files } = useFetchFile(
    `${RRS_API}/${params.id}/files`,
    'api/stock/rrs',
  );

  const { data: statusHistory, isLoading: isLoadingHistory } = useFetchData<
    StatusHistory[]
  >(`${RRS_API}/${params.id}/status-logs`);

  const exportToPDF = () => {
    html2canvas(componentRef.current as HTMLDivElement).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const componentWidth = pdf.internal.pageSize.getWidth();
      const componentHeight = pdf.internal.pageSize.getHeight();
      pdf.addImage(imgData, 'PNG', 0, 0, componentWidth, componentHeight);
      pdf.save(`receiving_report_${data?.code}.pdf`);
    });
  };

  const isCurrentUserCanEdit = () => {
    return (
      user?.role === 'ROLE_ADMIN' ||
      isUserCanEdit(user?.moduleAccess, 'ReceivingReport')
    );
  };

  const items: MenuProps['items'] = [
    {
      label: 'Edit',
      key: 'edit',
      icon: <EditOutlined />,
      disabled:
        !isCurrentUserCanEdit() ||
        data?.status === 'APPROVED' ||
        data?.status === 'READY' ||
        data?.status === 'DONE' ||
        data?.status === 'CANCEL',
    },
    // Steps 20-22: what came in from the supplier goes on to whoever asked
    // for it. Raising it here copies the lines rather than retyping them.
    ...(isUserCanCreate(user?.moduleAccess, 'Internal DR')
      ? [
          {
            label: 'Send to requestor (DR)',
            key: 'deliver',
            icon: <SendOutlined />,
          },
        ]
      : []),
    {
      label: 'Download',
      key: 'download',
      icon: <DownloadOutlined />,
      onClick: exportToPDF,
    },
    {
      label: 'Print',
      key: 'print',
      icon: <PrinterOutlined />,
      onClick: handlePrint, // Trigger print action directly when clicked
    },
  ];

  const onClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'edit') {
      navigate('/stock-movement/receiving-report/request/' + data?.id);
    }
    if (key === 'deliver' && data?.id) {
      generateDr(data.id, {
        onSuccess: (dr: { id?: string; code?: string }) => {
          message.success(`Delivery receipt ${dr?.code ?? ''} raised`);
          navigate(`/stock-movement/delivery-receipt/${dr?.id}`);
        },
        onError: (e: any) =>
          message.error(
            e?.response?.data?.message ?? 'Could not raise a delivery receipt.',
          ),
      });
    }
  };
  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };
  const handleClose = () => {
    navigate('/stock-movement/receiving-report');
  };
  useEffect(() => {
    if (isError) {
      setIsErrorModalOpen(isError);
      setErrorMess(error?.response?.data.message);
    }
  }, [isError]);

  if (isLoading && isLoadingHistory) {
    return <ReceivingReportInfoLoadingView active={true} />;
  }
  return (
    <>
      <div
        className={cn(
          'flex items-center justify-between',
          isLoading ? 'hidden' : '',
        )}
      >
        <PageHeaderAction
          action={searchParams.get('action')}
          moduleName={'Receiving Report'}
        />
        <ActionsMenu items={items} onClick={onClick} onBack={handleClose} />
      </div>
      <div className={'flex'}>
        <ReceivingReportInfoContent
          data={data}
          attachments={files}
          api={RRS_API}
          recordId={params.id}
          canEdit={isUserCanEdit(user?.moduleAccess, 'ReceivingReport')}
        />
        <div className={'w-3/12'}>
          <ReceivingReportInfoStatus
            data={data}
            statusHistory={statusHistory !== undefined ? statusHistory : []}
          />
          <div className={'m-4 space-y-4'}>
            <TrackingChain api={RRS_API} id={params.id} />
            <AuditCard
              createdBy={data?.createdBy}
              updatedBy={data?.updatedBy}
              timeCreated={data?.timeCreated}
              timeUpdated={data?.timeUpdated}
            />
          </div>
        </div>
      </div>
      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleErrorModalClose}
      />

      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
        <ReceivingReportPrintTemplate data={data} ref={componentRef} />
      </div>
    </>
  );
};
export default ReceivingReportInfoPage;
